import { fromPromise } from 'mobx-utils';
import { ApiReq } from 'src/api';
import { AxiosResponse } from 'axios';

interface CacheEntry<T> {
  inflightPromise?: ApiReq<T>;
  cachedResponse?: AxiosResponse<T | null>;
  timestamp: number;
  timerId?: NodeJS.Timeout;
}

const DEFAULT_CACHE_DURATION = 5000;
const isCachingEnabled = process.env.REACT_APP_NOT_NECESSARY_CACHING === 'true';
export function cacheRequest<T>(cacheDurationMs: number = DEFAULT_CACHE_DURATION) {
  const cache = new Map<string, CacheEntry<T>>();

  const normalize = (params: any[]): any[] =>
    params.map(p => (p === null || p === undefined || p === '' ? null : p));

  const getKey = (params: any[]) => JSON.stringify(normalize(params));

  const clear = (key: string) => {
    const entry = cache.get(key);
    if (entry?.timerId) clearTimeout(entry.timerId);
    cache.delete(key);
  };

  return (params: any[], apiCall: () => ApiReq<T>): ApiReq<T> => {
    if (!isCachingEnabled) {
      return apiCall();
    }

    const key = getKey(params);
    const now = Date.now();
    const entry = cache.get(key);

    if (entry && now - entry.timestamp < cacheDurationMs) {
      if (entry.cachedResponse) {
        return fromPromise(Promise.resolve(entry.cachedResponse));
      }
      if (entry.inflightPromise) {
        return entry.inflightPromise;
      }
    }

    if (entry) clear(key);

    const promise = apiCall();

    cache.set(key, { inflightPromise: promise, timestamp: now });

    promise.then(
      response => {
        const e = cache.get(key);
        if (e?.timestamp === now) {
          const timerId = setTimeout(() => {
            if (cache.get(key)?.timestamp === now) cache.delete(key);
          }, cacheDurationMs);

          cache.set(key, { cachedResponse: response, timestamp: now, timerId });
        }
        return response;
      },
      error => {
        if (cache.get(key)?.timestamp === now) clear(key);
        throw error;
      }
    );

    return promise;
  };
}
